import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { SocialFeedService } from '../../services/social-feed.service';
import { SocialPoll, SocialPollOption, SocialPost } from '../../models/social-feed.model';

/**
 * A post's poll. Until you vote you just see the choices (results stay hidden
 * so the running tally doesn't sway you); after voting — or once the poll has
 * ended, or if it's your own — each choice shows its share of the votes.
 * You can change your vote until the poll ends.
 */
@Component({
  selector: 'app-social-poll',
  templateUrl: './social-poll.component.html',
  styleUrls: ['./social-poll.component.scss'],
  standalone: false,
})
export class SocialPollComponent {
  @Input({ required: true }) post!: SocialPost;

  voting = false;
  error = '';

  constructor(private socialFeedService: SocialFeedService, private cdr: ChangeDetectorRef) {}

  get poll(): SocialPoll | null {
    return this.post?.poll ?? null;
  }

  percent(option: SocialPollOption): number {
    const total = this.poll?.total_votes ?? 0;
    return total > 0 ? Math.round(((option.votes ?? 0) * 100) / total) : 0;
  }

  get footer(): string {
    const poll = this.poll;
    if (!poll) {
      return '';
    }
    const votes = `${poll.total_votes} ${poll.total_votes === 1 ? 'vote' : 'votes'}`;
    if (poll.is_closed) {
      return `${votes} · Poll ended`;
    }
    if (!poll.closes_at) {
      return votes;
    }
    const ms = new Date(poll.closes_at).getTime() - Date.now();
    const hours = Math.ceil(ms / 3_600_000);
    const left = hours >= 48 ? `${Math.ceil(hours / 24)} days` : hours >= 2 ? `${hours} hours` : 'less than an hour';
    return `${votes} · Ends in ${left}`;
  }

  vote(option: SocialPollOption): void {
    const poll = this.poll;
    if (!poll || this.voting || poll.is_closed || poll.my_vote === option.id) {
      return;
    }
    this.voting = true;
    this.error = '';
    this.socialFeedService.votePost(this.post.id, option.id).subscribe({
      next: (updated) => {
        this.post.poll = updated.poll;
        this.voting = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.error = err?.message || "Couldn't record your vote. Please try again.";
        this.voting = false;
        this.cdr.detectChanges();
      },
    });
  }
}
